import { AttributeSort } from "@ciq/shared/utils/sort"

export class DataShed {
  constructor(onDataChange) {
    this._values = []
    this._onDataChangeListener = onDataChange
  }

  getById(id) {
    if (!id) {
      return null
    }

    return this._values.find(v => v.id === id)
  }

  /**
   * Returns all values in the shed.
   *
   * @returns {Array} Direct reference to internal array (NOT a copy).
   *
   * IMPORTANT: This method intentionally returns the same reference on each call.
   * This is required for React dependency arrays - returning a new array each time
   * would cause useEffect/useMemo to re-run on every render.
   *
   * Change notification happens via onDataChange callback, not reference equality.
   */
  getAll() {
    if (!this._values) {
      return []
    }

    return this._values
  }

  filter(func) {
    return this.getAll().filter(func)
  }

  setAll(schoolId, values) {
    if (!values) {
      values = []
    }

    if (Array.isArray(values)) {
      this._values = values
    } else if (typeof values === "object") {
      this._values = values.data
    } else {
      throw new Error("Invalid values type")
    }

    this._handleChange(schoolId)
    this._onDataChangeListener && this._onDataChangeListener()
  }

  add(value) {
    if (!value) {
      return
    }

    this.addBulk([value])
  }

  addBulk(values) {
    if (!values?.length) {
      return
    }

    const ids = values.map(v => v.id)
    this._values = [...this._values.filter(v => !ids.includes(v.id)), ...values].sort(
      AttributeSort("name")
    )

    this._handleChange()
    this._onDataChangeListener && this._onDataChangeListener()
  }

  remove(id) {
    if (!id) {
      return
    }

    this.removeBulk([id])
  }

  removeBulk(ids) {
    if (!ids?.length) {
      return
    }

    const originalLength = this._values.length
    this._values = this._values.filter(v => !ids.includes(v.id))

    // Only trigger callbacks if something actually changed
    if (this._values.length !== originalLength) {
      this._handleChange()
      this._onDataChangeListener && this._onDataChangeListener()
    }
  }

  clear() {
    this._values = []
    // Note: Intentionally does NOT call _onDataChangeListener() unlike other mutation methods.
    // This allows callers like WirelessConfigDepo to batch clear operations without triggering
    // expensive recalculations after each shed is cleared. See WirelessConfigDepo._pauseCount pattern.
    this._handleChange()
  }

  _handleChange(schoolId) {}
}
